import { Node, mergeAttributes } from '@tiptap/core'

import { GeneralOptions } from '@/types'

import BorderButton from './components/BorderButton.vue'

export interface BorderOptions extends GeneralOptions<BorderOptions> {
  HTMLAttributes: Record<string, unknown>
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    border: {
      setBorder: (options?: { height?: string; color?: string }) => ReturnType
    }
  }
}

export const Border = Node.create<BorderOptions>({
  name: 'border',

  group: 'block',
  atom: true,
  selectable: true,

  addOptions() {
    return {
      ...this.parent?.(),
      HTMLAttributes: {},
      divider: false,
      spacer: false,
      toolbar: true,
      button: ({ editor }) => ({
        component: BorderButton,
        componentProps: {
          action: (options?: unknown) => {
            if (typeof options === 'object' && options !== null) {
              editor
                .chain()
                .focus()
                .setBorder(options as { height?: string; color?: string })
                .run()
            } else {
              editor.chain().focus().setBorder().run()
            }
          },
          editor,
          isActive: () => editor.isActive('border') || false,
          disabled: !editor.can().setBorder(),
          tooltip: 'Border',
        },
      }),
    }
  },

  addAttributes() {
    return {
      height: {
        default: '1',
        parseHTML: element => element.getAttribute('data-height') || '1',
        renderHTML: attributes => {
          return {
            'data-height': attributes.height,
          }
        },
      },
      color: {
        default: '#000000',
        parseHTML: element => element.getAttribute('data-color') || '#000000',
        renderHTML: attributes => {
          return {
            'data-color': attributes.color,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="border"]',
        priority: 100,
      },
    ]
  },

  renderHTML({ node, HTMLAttributes }) {
    const height = node.attrs.height || '1'
    const color = node.attrs.color || '#000000'

    return [
      'div',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-type': 'border',
        'data-height': height,
        'data-color': color,
        class: 'border-node',
      }),
      [
        'div',
        {
          class: 'border-line',
          style: `height: ${height}px; background-color: ${color};`,
        },
      ],
    ]
  },

  addCommands() {
    return {
      setBorder:
        (options = {}) =>
        ({ chain }) => {
          const { height = '1', color = '#000000' } = options
          return chain()
            .insertContent({
              type: this.name,
              attrs: { height, color },
            })
            .run()
        },
    }
  },

  addKeyboardShortcuts() {
    return {
      'Mod-Alt-b': () => this.editor.commands.setBorder(),
    }
  },
})
